/**
 * Навигация мини-аппа — по разделу 7.3 ТЗ. Чистые функции над состоянием: оболочка (main.tsx)
 * только хранит его и рисует верхний экран активной вкладки.
 *
 *  - [F-7.3.4] У каждой вкладки свой стек. Переключение вкладки стек НЕ сбрасывает: ушёл из
 *    карточки товара в «Действия» и вернулся — карточка на месте.
 *  - [F-7.3.5] Повторный тап по уже активной вкладке сбрасывает её стек до корня.
 *  - [F-7.3.6] Глубина стека не больше 4: глубже — верхний экран заменяется, а не добавляется.
 *  - [F-7.3.9], [F-7.3.11] «Назад» — только системная кнопка Telegram: видна, когда в стеке
 *    вкладки есть куда вернуться, на корне скрыта (тогда «назад» Telegram закрывает приложение).
 *  - [F-7.3.12] Вход по ссылке сразу на глубокий экран: под него подставляется его цепочка
 *    родителей до корня вкладки — «назад» ведёт вверх по разделу, а не закрывает приложение.
 *  - [F-7.4.3] Раздел «только владелец» сотрудник не открывает: корень вкладки и сообщение.
 *
 * Экран открывается В ТЕКУЩЕЙ вкладке, даже если его «домашняя» вкладка другая: из «Сводки»
 * в «Агенты» и обратно — это путь внутри «Сводки», и «назад» возвращает туда, откуда пришли.
 */
import { SCREENS, TABS, BY_CODE, isVisibleFor, type ScreenId, type ScreenSpec, type Tab } from './screens.js'

export type Role = 'owner' | 'staff'
/** from — data-testid элемента, с которого ушли: на него «назад» возвращает фокус. */
export interface Place { spec: ScreenSpec; param: string | null; from?: string | null }
export interface NavState { active: Tab; stacks: Record<Tab, Place[]> }
export interface NavResult { state: NavState; notice: string | null }

/** Глубина над корнем вкладки: корень + 4 экрана. */
export const MAX_DEPTH = 4

export const NOTICE_OWNER_ONLY = 'Раздел доступен только владельцу.'
/** Раздел, который владелец этому сотруднику не открыл. */
export const NOTICE_NOT_GRANTED = 'Этот раздел вам не открыт — доступ выдаёт владелец магазина.'
const noticeFor = (spec: ScreenSpec): string => (spec.owner_only ? NOTICE_OWNER_ONLY : NOTICE_NOT_GRANTED)
export const NOTICE_STALE_LINK = 'Ссылка устарела — открыли раздел целиком.'

/**
 * Естественный родитель экрана — куда «назад» ведёт при входе по ссылке. Экраны глубины 1
 * без записи здесь ведут на корень своей вкладки.
 */
export const PARENT: Partial<Record<ScreenId, ScreenId>> = {
  'S-08': 'S-07', 'S-09': 'S-07', 'S-10': 'S-09',
  'S-12': 'S-11',
  'S-14': 'S-13', 'S-15': 'S-14', 'S-17': 'S-16', 'S-19': 'S-18',
  'S-21': 'S-20', 'S-22': 'S-20',
  'S-24': 'S-23', 'S-27': 'S-26', 'S-31': 'S-30',
  'S-02': 'S-06', 'S-03': 'S-02',
  'S-43': 'S-42',
}

const rootOf = (t: Tab): ScreenSpec => SCREENS[TABS.find((x) => x.id === t)!.root]
const homeTab = (s: ScreenSpec): Tab => s.tab ?? 'T1'
const place = (spec: ScreenSpec, param: string | null = null): Place => ({ spec, param })

export function emptyStacks(): Record<Tab, Place[]> {
  return Object.fromEntries(TABS.map((t) => [t.id, [place(rootOf(t.id))]])) as Record<Tab, Place[]>
}

export const current = (s: NavState): Place => s.stacks[s.active].at(-1)!
export const canGoBack = (s: NavState): boolean => s.stacks[s.active].length > 1
/** Вкладка в таббаре подсвечивается та, в чьём стеке сейчас находимся. */
export const activeTab = (s: NavState): Tab => s.active

/** Цепочка от корня вкладки до экрана включительно: S-24 → [S-37, S-23, S-24]. */
export function chainTo(spec: ScreenSpec): ScreenSpec[] {
  const root = rootOf(homeTab(spec))
  const out: ScreenSpec[] = [spec]
  let cur: ScreenSpec = spec
  for (let guard = 0; guard < MAX_DEPTH && cur.id !== root.id; guard++) {
    const p = PARENT[cur.id]
    if (p === undefined) break
    cur = SCREENS[p]
    out.unshift(cur)
  }
  if (out[0]!.id !== root.id) out.unshift(root)
  return out.slice(-(MAX_DEPTH + 1))
}

/** Можно ли роли открыть экран: «только владелец» и разделы следующего релиза — нельзя. */
export const allowed = (spec: ScreenSpec, role: Role): boolean => isVisibleFor(spec, role)

/**
 * Начальное состояние по ссылке. Неизвестный код — «Сводка» с сообщением, а не молча ([F-7.3.16]);
 * пустой код — «Сводка» без сообщения.
 */
export function initialNav(code: string | null, param: string | null, role: Role): NavResult {
  const stacks = emptyStacks()
  if (code === null || code === '' || code === 'home') return { state: { active: 'T1', stacks }, notice: null }
  const spec = BY_CODE[code]
  if (spec === undefined) return { state: { active: 'T1', stacks }, notice: NOTICE_STALE_LINK }
  const tab = homeTab(spec)
  if (!allowed(spec, role)) return { state: { active: tab, stacks }, notice: noticeFor(spec) }
  // Экран с параметром без параметра (ссылка обрезана) — открываем его родителя.
  const target = spec.param !== undefined && (param === null || param === '') && PARENT[spec.id] !== undefined
    ? SCREENS[PARENT[spec.id]!] : spec
  const chain = chainTo(target).filter((s) => allowed(s, role))
  stacks[tab] = chain.map((s, k) => place(s, k === chain.length - 1 && s.id === spec.id ? param : null))
  return { state: { active: tab, stacks }, notice: target === spec ? null : NOTICE_STALE_LINK }
}

/** Открыть экран поверх текущего в АКТИВНОЙ вкладке. */
export function openIn(s: NavState, id: ScreenId, param: string | null, role: Role, from: string | null = null): NavResult {
  const spec = SCREENS[id]
  if (!allowed(spec, role)) return { state: s, notice: noticeFor(spec) }
  const stack = s.stacks[s.active]
  const top = stack.at(-1)!
  // Тот же экран с тем же параметром — не плодим дубль в стеке.
  if (top.spec.id === id && top.param === param) return { state: s, notice: null }
  // Корень вкладки — это не «вглубь», а переход к корню: стек сворачивается до него.
  const rootIdx = stack.findIndex((p) => p.spec.id === id && p.param === param)
  if (rootIdx >= 0) return { state: { ...s, stacks: { ...s.stacks, [s.active]: stack.slice(0, rootIdx + 1) } }, notice: null }
  const below = stack.slice(0, -1)
  const next: Place[] = stack.length - 1 >= MAX_DEPTH
    ? [...below, place(spec, param)]                                // глубже нельзя — заменяем верхний
    : [...below, { ...top, from }, place(spec, param)]
  return { state: { ...s, stacks: { ...s.stacks, [s.active]: next } }, notice: null }
}

/**
 * Перейти к разделу из бокового меню компьютерной раскладки: его вкладка становится активной, а её стек —
 * цепочкой от корня до раздела (как при входе по ссылке). Прежний стек этой вкладки не сохраняется.
 */
export function jumpTo(s: NavState, id: ScreenId, role: Role): NavResult {
  const spec = SCREENS[id]
  if (!allowed(spec, role)) return { state: s, notice: noticeFor(spec) }
  const tab = homeTab(spec)
  const chain = chainTo(spec).filter((x) => allowed(x, role))
  const top = s.stacks[tab].at(-1)
  if (s.active === tab && s.stacks[tab].length === chain.length && top?.spec.id === id && top.param === null) return { state: s, notice: null }
  return { state: { active: tab, stacks: { ...s.stacks, [tab]: chain.map((x) => place(x)) } }, notice: null }
}

/** Заменить верхний экран (например, «отчёт не найден» → «все отчёты»), не углубляясь. */
export function replaceTop(s: NavState, id: ScreenId, param: string | null, role: Role): NavResult {
  const spec = SCREENS[id]
  if (!allowed(spec, role)) return { state: s, notice: noticeFor(spec) }
  const stack = s.stacks[s.active]
  const below = stack.slice(0, -1)
  // Новый экран уже лежит ниже в стеке — возвращаемся к нему, а не кладём второй экземпляр.
  const idx = below.findIndex((p) => p.spec.id === id && p.param === param)
  const next = idx >= 0 ? below.slice(0, idx + 1) : stack.length === 1 ? [place(spec, param)] : [...below, place(spec, param)]
  return { state: { ...s, stacks: { ...s.stacks, [s.active]: next } }, notice: null }
}

/** Системная «назад»: снять верхний экран активной вкладки. На корне — ничего (кнопка скрыта). */
export function backIn(s: NavState): NavState {
  const stack = s.stacks[s.active]
  if (stack.length <= 1) return s
  return { ...s, stacks: { ...s.stacks, [s.active]: stack.slice(0, -1) } }
}

/** Таббар: другая вкладка — к её стеку как был; та же — сброс до корня ([F-7.3.5]). */
export function pickTab(s: NavState, t: Tab): NavState {
  if (t !== s.active) return { ...s, active: t }
  return { ...s, stacks: { ...s.stacks, [t]: s.stacks[t].slice(0, 1) } }
}
